import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { LOGO, NAV_LINKS, CALL_LINK } from "@/data/content";

export default function Navbar({ scrollTo }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "border-b border-brand-line bg-brand-bg/80 shadow-sm backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-3"
        >
          <img src={LOGO} alt="Abhi Institute of Hotel Management" className="h-10 w-auto" />
          <span className="hidden font-mono text-[10px] uppercase leading-tight tracking-[0.18em] text-brand-muted lg:block">
            Institute of Hotel<br />Management · Delhi
          </span>
        </button>

        <nav className="hidden items-center gap-4 md:flex">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={() => go(link.id)}
              className="text-sm font-medium text-brand-ink/70 transition-colors duration-300 hover:text-brand-terra"
            >
              {link.label}
            </button>
          ))}
          <a
            data-testid="nav-scholarship-link"
            href="/scholarship-exam"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-brand-terra px-4 py-2.5 text-xs font-extrabold uppercase tracking-wide text-white shadow-md transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-ink"
          >
            Scholarship Exam
            <span data-testid="nav-scholarship-free-badge" className="rounded-full bg-white px-1.5 py-0.5 text-[9px] font-black leading-none tracking-wider text-brand-terra">FREE</span>
          </a>
          <a
            data-testid="nav-admission-link"
            href="/admission-form"
            className="inline-flex items-center whitespace-nowrap rounded-full bg-brand-green px-4 py-2.5 text-xs font-extrabold uppercase tracking-wide text-white shadow-md transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-ink"
          >
            Admission Form
          </a>
          <a
            data-testid="nav-call-now-button"
            href={CALL_LINK}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-ink px-4 py-2.5 text-xs font-semibold text-white transition-colors duration-300 hover:bg-brand-terra"
          >
            <Phone className="h-4 w-4" />
            Call Now
          </a>
          <button
            data-testid="nav-enquire-button"
            onClick={() => go("#enquire")}
            className="group inline-flex items-center gap-1.5 rounded-full bg-brand-green px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-brand-ink"
          >
            Enquire
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <div className="flex items-center gap-2 md:hidden">
          <a
            data-testid="nav-call-now-mobile"
            href={CALL_LINK}
            aria-label="Call Now"
            className="rounded-full bg-brand-ink p-2.5 text-white"
          >
            <Phone className="h-5 w-5" />
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-brand-line bg-brand-bg/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  onClick={() => go(link.id)}
                  className="py-3 text-left font-serif text-2xl text-brand-ink transition-colors hover:text-brand-terra"
                >
                  {link.label}
                </button>
              ))}
              <a
                data-testid="mobile-nav-scholarship-link"
                href="/scholarship-exam"
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-brand-terra px-6 py-3 font-sans text-sm font-extrabold uppercase tracking-wide text-white shadow-md"
              >
                Scholarship Exam
                <span data-testid="mobile-nav-scholarship-free-badge" className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black leading-none tracking-wider text-brand-terra">FREE</span>
              </a>
              <a
                data-testid="mobile-nav-admission-link"
                href="/admission-form"
                className="mt-2 inline-flex w-fit items-center rounded-full bg-brand-green px-6 py-3 font-sans text-sm font-extrabold uppercase tracking-wide text-white shadow-md"
              >
                Admission Form
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
